import type { BaseDocument } from '../mongo/base-document.interface';
import type { SyncResource } from '../sync/sync-resource';

export const ORDER_TYPE_SYSTEMIC_CODES = {
  CART_DROP_OFF: 'cartdropoff',
  ECOMMERCE: 'e-commerce',
} as const;

export type OrderTypeSystemicCode = (typeof ORDER_TYPE_SYSTEMIC_CODES)[keyof typeof ORDER_TYPE_SYSTEMIC_CODES];

export interface SystemicOrderTypeDefinition {
  code: OrderTypeSystemicCode;
  label: string;
}

/** The 2 built-in order types every tenant gets — seeded by `OrderTypeService.seedSystemicOrderTypes`. */
export const SYSTEMIC_ORDER_TYPES: SystemicOrderTypeDefinition[] = [
  { code: ORDER_TYPE_SYSTEMIC_CODES.CART_DROP_OFF, label: 'Cart Drop Off' },
  { code: ORDER_TYPE_SYSTEMIC_CODES.ECOMMERCE, label: 'E-commerce' },
];

export interface OrderType extends BaseDocument {
  code: string;
  isSystemic: boolean;
  label: string;
}

/**
 * Minimal OrderType reference embedded (denormalized) into other documents.
 */
export type OrderTypeSummary = Pick<OrderType, 'id' | 'code' | 'label'>;

/** Payload for creating a new OrderType. `isSystemic` defaults to false for user-created types. */
export type CreateOrderType = Pick<OrderType, 'label' | 'code'>;

/** Only the label can be updated. Systemic types cannot be updated. */
export type UpdateOrderType = Pick<OrderType, 'label'>;

// ─── Sync resource descriptor (declaration-merged with the OrderType interface) ─

/**
 * Runtime descriptor for OrderType collection synchronization.
 * Use `@SyncSource(OrderType)` on a service to publish create/update/delete events.
 * The topic names derived from `name: 'order-type'` are:
 *   on-order-type-created / on-order-type-updated / on-order-type-deleted
 */
export const OrderType: SyncResource<OrderType> = {
  collection: 'ordertypes',
  name: 'order-type',
};
